import type { Ancestor } from "@/entities/ancestor";
import type { InterventionCreatePayload } from "@/entities/intervention";
import type { Recommendation } from "@/entities/recommendation";
import type { Nullable } from "@/types";

import { Skeleton } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import { useCreate } from "@/hooks/queries";
import { useNavigateToRessource } from "@/hooks/useNavigateToRessource";
import { getAssetSelectOptionValue } from "@/modules/assets/assets-select-parent";
import { useInterventionTemplateValue } from "@/modules/intervention-template/intervention-template-select";
import { getLocationSelectOptionValue } from "@/modules/location/location-select-parent";
import { useOrganizationContextDetail } from "@/modules/organization/organization-context";

import InterventionForm from "./InterventionForm";

const invalidateKeys = [["interventions", "get"]];

type InterventionCreateFormProps = {
  ancestors?: Ancestor[];
  max_days_delay?: Nullable<number>;
  recommendation?: Recommendation;
  redirectToModel?: boolean;
};

const Create = ({
  ancestors = [],
  max_days_delay,
  recommendation,
  redirectToModel = true,
}: InterventionCreateFormProps) => {
  const { t } = useTranslation();
  const mutation = useCreate("interventions", { invalidateKeys });

  const onNavigateToIntervention =
    useNavigateToRessource<InterventionCreatePayload>(
      "intervention",
      ({ data }) => data.data.id
    );

  const { contextOrganization } = useOrganizationContextDetail();

  const {
    value: templateValue,
    defaultTemplate,
    isLoading: isLoadingTemplate,
    type: interventionType,
  } = useInterventionTemplateValue(ancestors);

  const initialValues = {
    location: getLocationSelectOptionValue(ancestors),
    asset: getAssetSelectOptionValue(ancestors),
    recommendation: {
      value: recommendation?.id,
      label: recommendation?.name,
      key: recommendation?.id,
    },
    application_date: dayjs(),
    scheduled_date: "",
    cost_type: "capex",
    max_days_delay,
    interventionType,
    template: templateValue,
    provider: contextOrganization?.name,
  };

  if (isLoadingTemplate) {
    return <Skeleton active />;
  }

  return (
    <InterventionForm
      mutation={mutation}
      saveButtonLabel={t("global:add")}
      initialValues={initialValues}
      defaultTemplate={defaultTemplate}
      onMutate={redirectToModel ? onNavigateToIntervention : undefined}
    />
  );
};

export default Create;
